import { determineExecutionRelationsFromEdges } from './allocationEdgeDraft';
import { executionRelationsToAllocationLinks } from './executionAuthorityResolver';
import type { RouteStopSaveRow } from './routeStopsSavePipeline';
import type {
  AllocationEdgeDraft,
  FromStopDraftByUid,
  QuantityDraftByCargo,
  ToStopDraftByUid
} from './executionDraftTypes';

export type LoadingUnloadingAllocationLink = {
  fromLoadingPosition: number;
  toUnloadingPosition: number;
};

/** Build structural loading→unloading links from Step 4 allocation drafts (uid authority). */
export function buildAllocationLinksFromUidDrafts(params: {
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  positionByStopUid: Map<string, number>;
  allocationEdges?: AllocationEdgeDraft[];
}): LoadingUnloadingAllocationLink[] {
  if (params.allocationEdges && params.allocationEdges.length > 0) {
    const relations = determineExecutionRelationsFromEdges(
      params.allocationEdges,
      params.positionByStopUid
    );
    return executionRelationsToAllocationLinks(relations);
  }

  const links: LoadingUnloadingAllocationLink[] = [];
  const seen = new Set<string>();

  for (const cargoId of params.selectedIds) {
    const qty = Number(params.quantityDraft[cargoId] ?? 0);
    if (qty <= 0) continue;
    const fromUid = params.fromStopDraft[cargoId];
    const toUid = params.toStopDraft[cargoId];
    if (!fromUid || !toUid) continue;
    const from = params.positionByStopUid.get(fromUid);
    const to = params.positionByStopUid.get(toUid);
    if (from === undefined || to === undefined) continue;
    const key = `${from}->${to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    links.push({ fromLoadingPosition: from, toUnloadingPosition: to });
  }

  return links;
}

/** @deprecated use buildAllocationLinksFromUidDrafts — position-based legacy adapter */
export function buildAllocationLinksFromDrafts(params: {
  selectedIds: number[];
  fromStopDraft: Record<number, number | undefined>;
  toStopDraft: Record<number, number | undefined>;
  quantityDraft: Record<number, number>;
}): LoadingUnloadingAllocationLink[] {
  const links: LoadingUnloadingAllocationLink[] = [];
  const seen = new Set<string>();

  for (const cargoId of params.selectedIds) {
    const qty = Number(params.quantityDraft[cargoId] ?? 0);
    if (qty <= 0) continue;
    const from = params.fromStopDraft[cargoId];
    const to = params.toStopDraft[cargoId];
    if (from === undefined || to === undefined) continue;
    const key = `${from}->${to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    links.push({ fromLoadingPosition: from, toUnloadingPosition: to });
  }

  return links;
}

export function isLoadingPositionLinkedByAllocations(
  loadingPos: number,
  allocationLinks: LoadingUnloadingAllocationLink[]
): boolean {
  return allocationLinks.some((l) => l.fromLoadingPosition === loadingPos);
}

/**
 * Pass-through for save payload — allocation links are sent as metadata to backend.
 * Does NOT clone unload rows (fake rows break group ownership validation on backend).
 */
export function ensureSavePayloadLoadingUnloadingLinks(
  rows: RouteStopSaveRow[],
  _allocationLinks: LoadingUnloadingAllocationLink[] = []
): RouteStopSaveRow[] {
  return rows;
}

export type StopsTopologyAudit = {
  rows: Array<{
    position: number;
    type: string;
    stopUid?: string;
    groupUid?: string;
    parentLoadingPosition?: number | null;
  }>;
  duplicatePositions: number[];
  duplicateStopUids: string[];
  orphanUnloadings: number[];
  unlinkedLoadings: number[];
  allocationLinks: LoadingUnloadingAllocationLink[];
};

export function auditStopsTopology(
  rows: RouteStopSaveRow[],
  allocationLinks: LoadingUnloadingAllocationLink[] = []
): StopsTopologyAudit {
  const positions = rows.map((r) => r.position);
  const positionCounts = new Map<number, number>();
  for (const p of positions) {
    positionCounts.set(p, (positionCounts.get(p) ?? 0) + 1);
  }
  const duplicatePositions = Array.from(positionCounts.entries())
    .filter(([, count]) => count > 1)
    .map(([pos]) => pos);

  const uids = rows.map((r) => r._stopUid).filter(Boolean) as string[];
  const uidCounts = new Map<string, number>();
  for (const uid of uids) {
    uidCounts.set(uid, (uidCounts.get(uid) ?? 0) + 1);
  }
  const duplicateStopUids = Array.from(uidCounts.entries())
    .filter(([, count]) => count > 1)
    .map(([uid]) => uid);

  const loadingPositions = new Set(
    rows.filter((r) => r.type === 'loading').map((r) => r.position)
  );
  const unloadingParents = new Set(
    rows
      .filter((r) => r.type === 'unloading')
      .map((r) => Number(r.parentLoadingPosition ?? 0))
  );

  const orphanUnloadings = rows
    .filter(
      (r) =>
        r.type === 'unloading' && !loadingPositions.has(Number(r.parentLoadingPosition ?? 0))
    )
    .map((r) => r.position);

  const unlinkedLoadings = Array.from(loadingPositions).filter(
    (pos) =>
      pos !== 0 &&
      !unloadingParents.has(pos) &&
      !isLoadingPositionLinkedByAllocations(pos, allocationLinks)
  );

  return {
    rows: rows.map((r) => ({
      position: r.position,
      type: r.type,
      stopUid: r._stopUid,
      groupUid: r._groupUid,
      parentLoadingPosition: r.parentLoadingPosition
    })),
    duplicatePositions,
    duplicateStopUids,
    orphanUnloadings,
    unlinkedLoadings,
    allocationLinks
  };
}

export function logStopsSavePayload(
  stage: 'before-links' | 'after-links' | 'final',
  rows: RouteStopSaveRow[],
  allocationLinks: LoadingUnloadingAllocationLink[] = []
): void {
  if (process.env.NODE_ENV === 'production') return;
  const audit = auditStopsTopology(rows, allocationLinks);
  console.group(`[StopsSavePayload] stage=${stage}`);
  console.table(audit.rows);
  console.log('[StopsTopology]', {
    allocationLinks: audit.allocationLinks,
    duplicatePositions: audit.duplicatePositions,
    duplicateStopUids: audit.duplicateStopUids,
    orphanUnloadings: audit.orphanUnloadings,
    unlinkedLoadings: audit.unlinkedLoadings,
    parentLinks: audit.rows
      .filter((r) => r.type === 'unloading')
      .map((r) => ({ position: r.position, parentLoadingPosition: r.parentLoadingPosition, groupUid: r.groupUid }))
  });
  console.groupEnd();
}

export function logStopsTopologyDebug(
  label: string,
  rows: RouteStopSaveRow[],
  allocationLinks: LoadingUnloadingAllocationLink[] = []
): void {
  if (process.env.NODE_ENV === 'production') return;
  const audit = auditStopsTopology(rows, allocationLinks);
  console.group(`[StopsTopology] ${label}`);
  console.log('topology graph', audit.rows);
  console.log('links', audit.allocationLinks);
  console.log('parent positions', audit.rows.filter((r) => r.type === 'unloading'));
  console.log('group ownership', audit.rows.map((r) => ({ uid: r.stopUid, group: r.groupUid, type: r.type })));
  console.log('duplicate checks', {
    positions: audit.duplicatePositions,
    stopUids: audit.duplicateStopUids
  });
  console.groupEnd();
}
